import type { FC, RefObject } from 'react';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';
import cn from 'classnames';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';

import type { TMainHeader } from './models';
import { useShow } from './helpers/use-show';
import { getScrollbarWidth } from './helpers/get-scrollbar-width';

function useHandleChangeShowSearch(
    isShowSearch: boolean,
    yandexSearchBarRef: RefObject<HTMLInputElement>
) {
    useLayoutEffect(() => {
        if (!isShowSearch) return () => {};
        // Порядок важен. Сначала высчитываем ширину, потом убираем скролл
        document.body.style.paddingRight = `${getScrollbarWidth()}px`;
        document.body.style.overflow = 'hidden';

        if (yandexSearchBarRef.current) {
            yandexSearchBarRef.current.focus();
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isShowSearch, yandexSearchBarRef]);
}

export const MainHeader: FC<React.PropsWithChildren<TMainHeader>> = props => {
    const {
        ApplicationsButton,
        BannerLink,
        IconAlfaLogo,
        InternetBank,
        Layout,
        SearchIcon,
        SubsectionLink,
        TopRow,
        YandexSearchBar,
        applicationsButton,
        applicationsButtonAnalytics,
        bottomRow,
        children,
        dataTestId = 'MainHeader',
        defaultTheme = 'light',
        dropDown,
        height,
        internetBank,
        internetBankAnalytics,
        logo,
        styles,
        withSearch = true,
    } = props;

    const { isShown: isShowSearch, toggle: toggleSearch } = useShow();
    const { isShown: isShownTopRow, toggle: toggleTopRowShow } = useShow();
    const [isHovered, setHovered] = useState(false);
    const { logo: logoSlot } = useNamedContainers(children);

    const [, cancel] = useDebounce(
        () => {
            if (!isHovered && isShownTopRow) {
                toggleTopRowShow();

                return;
            }

            cancel();
        },
        400,
        [isHovered]
    );

    const yandexSearchBarRef = useRef<HTMLInputElement>(null);

    useHandleChangeShowSearch(isShowSearch, yandexSearchBarRef);

    const colorTheme = cn(styles[defaultTheme], styles.external, {
        [styles.withHeight]: height !== undefined,
    });

    const handleMouseLeave = () => setHovered(false);
    const handleMouseOver = () => setHovered(true);

    const renderBottomRow = useMemo(
        () =>
            bottomRow.map(({ analytics, key, link, title }) => (
                <SubsectionLink
                    key={key}
                    analytics={analytics}
                    dropDownData={dropDown[key]}
                    link={link}
                    title={title}
                />
            )),
        [bottomRow, dropDown]
    );
    const wrapperStyle = height === undefined ? {} : { height };

    return (
        <Layout className={colorTheme} dataTestId={`${dataTestId}Layout`} style={wrapperStyle}>
            <div className={colorTheme} style={wrapperStyle}>
                <div
                    className={styles.container}
                    onMouseEnter={handleMouseOver}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className={styles.containerLogo}>
                        {logoSlot || (
                            <BannerLink link={logo?.link} title={logo?.title}>
                                <IconAlfaLogo
                                    // eslint-disable-next-line external-classname/specificity
                                    className={styles.iconLogo}
                                    data-test-id={`${dataTestId}Logo`}
                                    height={47}
                                    width={34}
                                />
                            </BannerLink>
                        )}
                    </div>
                    <div className={styles.containerLeft}>
                        <TopRow
                            handleClick={toggleTopRowShow}
                            showAll={isShownTopRow}
                            theme={defaultTheme}
                            {...props}
                        />
                        <div>{renderBottomRow}</div>
                    </div>
                    <div
                        className={cn(
                            styles.containerRight,
                            isShownTopRow && styles.hideContainerRight
                        )}
                    >
                        <div className={cn(styles.controls, isShowSearch && styles.fadeOut)}>
                            {withSearch && <SearchIcon onClick={toggleSearch} />}
                            {applicationsButton && (
                                <ApplicationsButton
                                    analytics={applicationsButtonAnalytics}
                                    applicationsButton={applicationsButton}
                                    theme={defaultTheme}
                                />
                            )}

                            <InternetBank
                                analytics={internetBankAnalytics}
                                internetBank={internetBank}
                            />
                        </div>
                        <div className={cn(styles.searchHolder, isShowSearch && styles.fadeIn)}>
                            <YandexSearchBar inputInnerRef={yandexSearchBarRef} />
                        </div>
                        <div
                            className={cn(styles.searchOverlay, isShowSearch && styles.fadeIn)}
                            data-test-id="main-header-search-overlay"
                            onClick={toggleSearch}
                        />
                    </div>
                </div>
            </div>
        </Layout>
    );
};
